import React, { useState } from 'react';
import { Exercise, MuscleGroup } from '../database';
import { useFormHandler } from '../hooks/useFormHandler';
import { createCrudOperations } from '../utils/crud';
import ErrorDisplay from '../components/ErrorDisplay';

const ExerciseForm: React.FC = () => {
  const exerciseCrud = createCrudOperations<Exercise>('exercises');
  
  const {
    isEditing,
    submitting,
    error,
    initialData,
    defaultValues,
    handleSubmit,
    handleCancel,
  } = useFormHandler<Exercise>({
    ...exerciseCrud,
    redirectPath: '/exercises',
    defaultValues: {
      name: '',
      description: undefined,
      category: undefined,
      primaryMuscles: undefined,
      secondaryMuscles: undefined,
    },
  });

  const [name, setName] = useState(initialData?.name || defaultValues.name);
  const [description, setDescription] = useState(initialData?.description || '');
  const [category, setCategory] = useState(initialData?.category || '');
  const [primaryMuscles, setPrimaryMuscles] = useState<MuscleGroup[]>(initialData?.primaryMuscles || []);
  const [secondaryMuscles, setSecondaryMuscles] = useState<MuscleGroup[]>(initialData?.secondaryMuscles || []);

  React.useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setDescription(initialData.description || '');
      setCategory(initialData.category || '');
      setPrimaryMuscles(initialData.primaryMuscles || []);
      setSecondaryMuscles(initialData.secondaryMuscles || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData?.id]); // Only update when ID changes to avoid infinite loops

  const onSubmit = () => {
    if (!name.trim()) return;
    
    handleSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      category: category.trim() || undefined,
      primaryMuscles: primaryMuscles.length > 0 ? primaryMuscles : undefined,
      secondaryMuscles: secondaryMuscles.length > 0 ? secondaryMuscles : undefined,
    });
  };

  const categories = ['Push', 'Pull', 'Legs', 'Core', 'Cardio', 'Functional', 'Machine'];
  
  const muscles: MuscleGroup[] = [
    // Major muscle groups
    'Chest', 'Back', 'Lats', 'Rhomboids', 'Traps', 'Shoulders',
    'Front Delts', 'Side Delts', 'Rear Delts', 'Biceps', 'Triceps', 'Forearms',
    'Core', 'Abs', 'Obliques', 'Quads', 'Hamstrings', 'Glutes', 'Calves',
    // Special categories
    'Cardiovascular', 'Full Body'
  ];

  const toggleMuscle = (muscle: MuscleGroup, type: 'primary' | 'secondary') => {
    if (type === 'primary') {
      setPrimaryMuscles(prev => 
        prev.includes(muscle) 
          ? prev.filter(m => m !== muscle)
          : [...prev, muscle]
      );
    } else {
      setSecondaryMuscles(prev => 
        prev.includes(muscle) 
          ? prev.filter(m => m !== muscle)
          : [...prev, muscle]
      );
    }
  };

  // No loading spinner needed for local data - loads instantly

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <button
          onClick={handleCancel}
          className="text-blue-600"
        >
          ← Back
        </button>
        <h2 className="text-xl font-bold text-gray-900">
          {isEditing ? 'Edit Exercise' : 'New Exercise'}
        </h2>
      </div>

      <ErrorDisplay error={error} />

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Exercise Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g., Bench Press"
            autoFocus
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select category</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* Primary Muscles */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Primary Muscles
          </label>
          <div className="flex flex-wrap gap-2">
            {muscles.map(muscle => (
              <button
                key={muscle}
                type="button"
                onClick={() => toggleMuscle(muscle, 'primary')}
                className={`px-3 py-1 rounded-full text-sm ${
                  primaryMuscles.includes(muscle)
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {muscle}
              </button>
            ))}
          </div>
        </div>

        {/* Secondary Muscles */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Secondary Muscles
          </label>
          <div className="flex flex-wrap gap-2">
            {muscles.map(muscle => (
              <button
                key={muscle}
                type="button"
                onClick={() => toggleMuscle(muscle, 'secondary')}
                className={`px-3 py-1 rounded-full text-sm ${
                  secondaryMuscles.includes(muscle)
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {muscle}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            rows={3}
            placeholder="Exercise notes or instructions (optional)"
          />
        </div>

        <div className="flex space-x-3 relative z-10">
          <button
            onClick={onSubmit}
            disabled={!name.trim() || submitting}
            className="flex-1 bg-blue-600 text-white py-2 rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed cursor-pointer hover:bg-blue-700"
            style={{ pointerEvents: 'auto' }}
          >
            {submitting ? 'Saving...' : 'Save Exercise'}
          </button>
          <button
            onClick={handleCancel}
            disabled={submitting}
            className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg disabled:opacity-50 cursor-pointer hover:bg-gray-400"
            style={{ pointerEvents: 'auto' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExerciseForm;